import useAppNavigation from '@/hooks/useAppNavigation';
import { format } from 'date-fns';
import type React from 'react';
import { Link } from 'react-router';
import { ROUTES } from '../../../constants/Constants';
import type { AppSsrFetchedDataType } from '../../../server/AppSsr';

interface TermsAndConditionsProps {
  ssrFetchedData: AppSsrFetchedDataType;
}

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({
  ssrFetchedData,
}) => {
  const { toLocalizedRoute } = useAppNavigation();
  const lastConditionsUpdate = new Date('2024-09-23');

  const companyName = ssrFetchedData.appConfiguration.company.owner;
  const serviceName = ssrFetchedData.appConfiguration.service.name;
  const serviceWebsite = ssrFetchedData.domain;
  const companyLegalEntityName =
    ssrFetchedData.appConfiguration.company.legacy_entity_name;
  const companyLegalEntityAddress =
    ssrFetchedData.appConfiguration.company.legacy_entity_address;
  const companyLegalEntityVATNumber =
    ssrFetchedData.appConfiguration.company.legacy_entity_vat_number;
  const companyLegalEntityCF =
    ssrFetchedData.appConfiguration.company.legacy_entity_cf;

  const mainLanguageDisclaimer = () => (
    <section className="mb-8 font-thin">
      <div className="mt-4 italic">
        You’re reading the English version of the privacy policy. Among the
        various translated versions, the text of the Italian version will always
        prevail over the other versions in case of different interpretations
        and/or discrepancies.
      </div>
    </section>
  );

  return (
    <div className="flex flex-col items-center p-4">
      <div className="mx-2 max-w-7xl md:mx-8">
        <div className="mx-2 flex max-w-3xl flex-col items-start md:mx-8">
          <section>
            <div className="flex shrink-0 flex-col justify-between">
              <h1 className="mb-2 text-2xl font-black">
                General Terms and Conditions of {serviceName}
              </h1>
              <h2 className="mb-2 text-sm font-semibold">
                Last updated: {format(lastConditionsUpdate, 'do MMMM yyyy')}
              </h2>
              {false && mainLanguageDisclaimer()}
            </div>
            <div className="my-8 font-normal">
              <div className="mb-6 text-xl font-bold">
                Brief summary for if you’re short on time
              </div>
              <section className="mb-3">
                We want to provide you with clear and transparent information
                about {serviceName}.
              </section>
              <section className="mb-3">
                We know that legal texts are as impenetrable as they are
                important, so to make it a bit simpler for you we’ve briefly
                explained here about who we are, what services we offer and what
                you can and cannot do. Below you will find the long version of
                the conditions, with all the further details of the case.
              </section>
              <section className="mt-6">
                <div className="mb-4 text-xl font-bold">Who are we?</div>
                <section className="mb-3">
                  {companyLegalEntityName} (hereinafter,{' '}
                  <span className="font-bold">
                    &quot;
                    {companyName}&quot;
                  </span>
                  ) is the entity that manages the Website, App and tools
                  (hereinafter, the{' '}
                  <span className="font-bold">&quot;Platform&quot;</span>
                  ). Our Italian offices are in {companyLegalEntityAddress}. We
                  are registered with VAT and Tax code no.{' '}
                  {companyLegalEntityVATNumber}. C.F. {companyLegalEntityCF}
                </section>
              </section>
              <section className="mt-6">
                <div className="mb-4 text-xl font-bold">
                  What services do we offer?
                </div>
                <section className="mb-3">
                  We offer an online space where you can post or search for
                  listings of specific professionals and businesses, whether
                  they are close to you or elsewhere.
                </section>
                <section className="mb-3">
                  We also offer other services related to the specific business
                  sector to provide you with a complete experience.
                </section>
              </section>
              <section className="mt-6">
                <div className="mb-4 text-xl font-bold">
                  What you can and cannot do on {serviceName}
                </div>
                <section className="mb-3">
                  You can use the Platform, sign up to be able to save your
                  searches and favorite professional services, contact
                  advertisers, publish your professional services, and hire
                  other additional services from us.
                </section>
                <section className="mb-3">
                  You may not damage third parties or {companyName}, break the
                  law, use automatic mechanisms to copy or extract our content,
                  create false contacts or accounts nor use the user access
                  details of other people without their permission.
                </section>
                <section className="mb-3">
                  If you are an advertiser, we recommend that you read our rules
                  for publishing listings in detail.
                </section>
              </section>
              <section className="mt-6">
                <div className="mb-4 text-xl font-bold">
                  Who owns the Platform and the relevant content?
                </div>
                <section className="mb-3">
                  {companyName} is the owner of the Platform and of all the
                  content, images, brands, logos, videos, texts, etc. that
                  appear on the Platform (or they are from third parties who
                  have given us their permission). By sharing material on{' '}
                  {serviceName}, you authorise us to keep using it even when
                  your listing is no longer on the website for our reports and
                  historic references of the {companyName} related markets, for
                  example.
                </section>
              </section>
              <section className="mt-6">
                <div className="mb-4 text-xl font-bold">Now what?</div>
                <section className="mb-3">
                  Now comes the part that will put your knowledge of the English
                  language to the test, where the legal jargon (which is what is
                  really legally binding) shines in all its glory. This might be
                  a good time to eat some dark chocolate and get ready. Here we
                  go!
                </section>
              </section>
              <hr className="my-8 h-px border-0 bg-gray-200 dark:bg-gray-700" />
              <section className="mt-10">
                <div className="mb-4 text-lg font-bold">Index</div>
                <ol className="list-decimal">
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.TERMS_AND_CONDITIONS_ROUTE}#section01`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Purpose and overview
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.TERMS_AND_CONDITIONS_ROUTE}#section02`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Access to the Platform and acceptance of the General
                      Conditions
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.TERMS_AND_CONDITIONS_ROUTE}#section03`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Children
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.TERMS_AND_CONDITIONS_ROUTE}#section04`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Conditions of use and responsibility for use of the
                      Platform
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.TERMS_AND_CONDITIONS_ROUTE}#section05`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Publishing listings
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.TERMS_AND_CONDITIONS_ROUTE}#section06`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Rules for publishing listings
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.TERMS_AND_CONDITIONS_ROUTE}#section07`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Additional services
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.TERMS_AND_CONDITIONS_ROUTE}#section08`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Intellectual and industrial property
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.TERMS_AND_CONDITIONS_ROUTE}#section09`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      {companyName}&apos;s responsibility
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.TERMS_AND_CONDITIONS_ROUTE}#section10`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Payments on the Platform
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.TERMS_AND_CONDITIONS_ROUTE}#section11`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Contracting with third parties through the Platform
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.TERMS_AND_CONDITIONS_ROUTE}#section12`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Links or hyperlinks to the Platform
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.TERMS_AND_CONDITIONS_ROUTE}#section13`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Personal data protection
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.TERMS_AND_CONDITIONS_ROUTE}#section14`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Term and Duration
                    </Link>
                  </li>
                  <li className="mb-1">
                    <Link
                      to={toLocalizedRoute(
                        `${ROUTES.TERMS_AND_CONDITIONS_ROUTE}#section15`,
                      )}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Applicable law and jurisdiction
                    </Link>
                  </li>
                </ol>
              </section>
              <section className="mt-16" id="section01">
                <div className="mb-4 text-xl font-bold">
                  1. Purpose and overview
                </div>
                <section className="mb-3">
                  <section className="mb-3">
                    {companyLegalEntityName} (hereinafter,{' '}
                    <span className="font-bold">&quot;{companyName}&quot;</span>
                    , or <span className="font-bold">&quot;we&quot;</span>)
                    applies the following terms and conditions (
                    <span className="font-bold">
                      &quot;General Conditions&quot;
                    </span>
                    ). These rule:
                    <ol className="ml-8 mt-4 list-disc">
                      <li className="mb-2">
                        use, browsing and, where relevant, registration, of the
                        website held under the domain {serviceWebsite} as well
                        as its respective subdomains (hereinafter, the{' '}
                        <span className="font-bold">&quot;Site&quot;</span> or{' '}
                        <span className="font-bold">&quot;Website&quot;</span>)
                        and the mobile apps (hereinafter, the{' '}
                        <span className="font-bold">
                          &quot;Applications&quot;
                        </span>{' '}
                        or <span className="font-bold">&quot;Apps&quot;</span>)
                        owned by {companyName} (hereinafter, the{' '}
                        <span className="font-bold">&quot;Platform&quot;</span>)
                      </li>
                      <li className="mb-2">
                        the downloading and use of any information, text, images
                        or other content (hereinafter, the{' '}
                        <span className="font-bold">&quot;Content&quot;</span>)
                        that you might access from the Platform.
                      </li>
                    </ol>
                    <section className="mt-4">
                      Our aim is to increase your awareness of what we do, how
                      our Platform work and the services we provide. At{' '}
                      {companyName}, we truly appreciate our users and the trust
                      you place in us when using our Platform is essential.
                      Therefore, we would like to repay this trust by giving you
                      information on our services that is clear and easy to
                      understand.
                    </section>
                    <section className="mt-4">
                      Below you will find the General Conditions and any other
                      terms and conditions of the services that constitute the
                      entire contract between {companyName} and its users.
                    </section>
                  </section>
                  <section className="my-6">
                    <div className="mb-4 text-lg font-bold">
                      What is {serviceName}?
                    </div>
                    <section className="mt-4">
                      Our website, hosted under the domain {serviceWebsite} and
                      its subdomains, and our Apps, contain a technology
                      platform that we make available to our users so they can
                      publish listings of their professional and business
                      profiles and services or search for a professional or
                      business (hereinafter the{' '}
                      <span className="font-bold">
                        &quot;Professional Services&quot;
                      </span>
                      ).
                    </section>
                    <section className="mt-4">
                      {companyName} is merely an intermediary that is limited to
                      facilitating contact between users offering professional
                      services and those who are looking for professional
                      services. {companyName} does not have any involvement in
                      any transactions that may arise from such contact. Within
                      this context, we provide some additional services
                      connected with the Platform we just described above
                      (hereinafter, the{' '}
                      <span className="font-bold">
                        &quot;Additional Services&quot;
                      </span>
                      ). There is more information on our Additional Services in
                      Clause 7 of these General Conditions.
                    </section>
                  </section>
                  <section className="mb-3">
                    <div className="mb-6 text-lg font-bold">
                      Who is {serviceName}?
                    </div>
                    <section className="mt-4">
                      {companyLegalEntityName} is the owner of this website and
                      the Platform, with registered office at{' '}
                      {companyLegalEntityAddress}, VAT and tax code no.{' '}
                      {companyLegalEntityVATNumber}, C.F. {companyLegalEntityCF}
                    </section>
                    <section className="mt-4">
                      Please feel free to contact us at any time by sending us
                      an email at privacy(at){serviceWebsite}.
                    </section>
                  </section>
                </section>
              </section>
              <section className="mt-16" id="section02">
                <div className="mb-4 text-xl font-bold">
                  2. Access to the Platform and acceptance of the General
                  Conditions
                </div>
                <section className="mb-3">
                  <section className="mt-4">
                    When accessing or using the Platform, you do so under your
                    own responsibility and assume the role of user (hereinafter,
                    <span className="font-bold">&quot;User&quot;</span>) and it
                    is assumed that you have read, understand and unreservedly
                    accept these General Conditions in their latest published
                    version. In the event that you use additional services
                    requiring registration, whether as a private user or
                    professional user, you will become a registered user
                    (hereinafter,{' '}
                    <span className="font-bold">
                      &quot;Registered User&quot;
                    </span>
                    ). This likewise assumes that you accept the specific
                    conditions that replace, add to and/or modify these General
                    Conditions (hereinafter,{' '}
                    <span className="font-bold">
                      &quot;Specific Conditions&quot;
                    </span>
                    ) as they apply in each case. There is more detailed
                    information on our Additional Services in Clause 7 of these
                    General Conditions.
                  </section>
                  <section className="mt-4">
                    We reserve the right to extend, modify, substitute and/or
                    cancel our Platform and/or these General Conditions at any
                    time, without prior notice and without giving rise to any
                    liability or indemnity on the part of {companyName}. We will
                    do our best to notify you of any changes to the General
                    Conditions. In particular, if you are a Registered User, we
                    will notify you of any changes to the Terms and Conditions
                    by email to the email address you provided to us during
                    registration. We will also change the date of the
                    &quot;latest update&quot; located at the top of these
                    General Conditions. However, we advise you to review these
                    General Conditions frequently to be aware of any changes we
                    may have made.
                  </section>
                  <section className="mt-4">
                    If you access the Platform or use the Professional Services
                    after any change is made to the General Conditions, you will
                    be expressly accepting the new General Conditions.
                  </section>
                  <section className="mt-4">
                    If you do not agree with these General Conditions or with
                    any changes that may be made to them, you will need to stop
                    using the Platform as well as the Professional Services. If
                    you continue to use them, it means that you accept the
                    General Conditions.
                  </section>
                  <section className="mt-4">
                    To clarify, the present General Conditions and the Specific
                    Conditions, where applicable, will apply to both private
                    users and professional users.
                  </section>
                </section>
              </section>
              <section className="mt-16" id="section03">
                <div className="mb-4 text-xl font-bold">3. Children</div>
                <section className="mb-3">
                  The Professional Services provided by {companyName} are not
                  intended for minors under 18 years old. By accessing or using
                  our Platform or Professional Services, you state and guarantee
                  that you are 18 years old or over.
                </section>
              </section>
              <section className="mt-16" id="section04">
                <div className="mb-4 text-xl font-bold">
                  4. Conditions of use and responsibility for use of the
                  Platform
                </div>
                <section className="mb-3">
                  <section className="mt-4">
                    Your use of the Platform must at all times comply with these
                    General Conditions and the Specific Conditions, where
                    applicable, as well as applicable law, good morals, decency
                    and public order.
                  </section>
                  <section className="mt-4">
                    You may not use any of the above unlawfully, inappropriately
                    or in any way that contravenes any provision in these
                    General Conditions or the Specific Conditions, as the case
                    may be. In order to facilitate your browsing and use of our
                    Platform, please see the following list of what you can and
                    cannot do:
                  </section>
                  <section className="mb-3 mt-6">
                    <div className="mb-6 text-lg font-bold">You can:</div>
                    <section className="mt-4">
                      <ol className="ml-8 mt-4 list-disc">
                        <li className="mb-2">
                          Access and browse the Platform without needing to
                          register, completely free of charge (without prejudice
                          to the Additional Services described in Clause 7 of
                          these General Conditions).
                        </li>
                        <li className="mb-2">
                          Receive new Professional Services listings or save
                          Professional Services that may be of interest as a
                          favourite.
                        </li>
                        <li className="mb-2">
                          List Professional Services that you own or manage.
                        </li>
                        <li className="mb-2">
                          Sign up for Additional Services connected with the
                          Professional Services you are listing on the Platform.
                        </li>
                      </ol>
                    </section>
                  </section>
                  <section className="mb-3 mt-6">
                    <div className="mb-6 text-lg font-bold">You cannot:</div>
                    <section className="mt-4">
                      <ol className="ml-8 mt-4 list-disc">
                        <li className="mb-2">
                          <div>Disseminate, share or publish:</div>
                          <ol className="ml-8 mt-4 list-disc">
                            <li className="mb-2">
                              Content that is racist, xenophobic, obscene or
                              derogatory.
                            </li>
                            <li className="mb-2">
                              Content that incites, involves, or promotes
                              criminal, violent or defamatory acts on the
                              grounds of age, gender, religion, beliefs, or that
                              involves the defence of terrorism.
                            </li>
                            <li className="mb-2">
                              Content that injures the fundamental rights of
                              third parties in any way.
                            </li>
                            <li className="mb-2">
                              Information that constitutes illicit or unfair
                              advertising.
                            </li>
                            <li className="mb-2">
                              Content that violates the privacy of communication
                              and/or the applicable law with regards to personal
                              data protection.
                            </li>
                            <li className="mb-2">
                              False, inaccurate, or fraudulent content that
                              could mislead the recipients of such information.
                            </li>
                          </ol>
                        </li>
                        <li className="mb-2">
                          Use the Platform for illicit or commercial purposes,
                          for the purpose of making a profit, or for the resale,
                          transfer or disposal for the use or benefit of any
                          other person or entity.
                        </li>
                        <li className="mb-2">
                          Cause damage to IT systems owned by {companyName} or
                          third parties or introduce or spread computer viruses
                          or anything else that could damage the IT systems.
                        </li>
                        <li className="mb-2">
                          Carry out acts that in any way infringe on the
                          Intellectual and Industrial Property rights of{' '}
                          {companyName} and/or jeopardise the reputation of{' '}
                          {companyName}, or of third parties.
                        </li>
                        <li className="mb-2">
                          Use the identity and/or access keys or password
                          belonging to another Registered User without their
                          consent.
                        </li>
                        <li className="mb-2">
                          Access, control or copy any content or information
                          included on the Platform using any kind of robot,
                          spider, scraper or any other automatic or manual
                          process to do so for any purpose, without our express
                          written permission.
                        </li>
                        <li className="mb-2">
                          Violate the restrictions contained in any notice on
                          the exclusion of robots included on this Website, or
                          bypass or circumvent other measures used to prevent or
                          limit access to this Website.
                        </li>
                        <li className="mb-2">
                          Take any action that imposes or that may impose an
                          unreasonable or disproportionately large load on our
                          infrastructure.
                        </li>
                        <li className="mb-2">
                          Set up a link to any content on the Platform for any
                          purpose without our express written permission.
                        </li>
                        <li className="mb-2">
                          Copy, display or otherwise incorporate any content
                          from the Platform into any other website or app
                          without our prior written permission.
                        </li>
                      </ol>
                    </section>
                  </section>
                  <section className="mb-3 mt-6">
                    <div className="mb-6 text-lg font-bold">
                      What happens if you do any of these things?
                    </div>
                    <section className="mt-4">
                      <section className="mt-4">
                        If you carry out any of the above-mentioned actions, you
                        will be liable for any damages arising from such direct
                        or indirect breach of these General Conditions and you
                        agree to hold {companyName} harmless.
                      </section>
                      <section className="mt-4">
                        In the event that these General Conditions or Specific
                        Conditions are breached, we reserve the right to
                        unilaterally cancel, at our sole discretion and without
                        prior notice, the access to, use of and/or registration
                        on our Platform and Professional Services of any User,
                        Registered User or Advertiser, without this in any way
                        giving rise to any form of compensation.
                      </section>
                    </section>
                  </section>
                  <section className="mb-3 mt-6">
                    <div className="mb-6 text-lg font-bold">
                      What happens if you find someone doing this?
                    </div>
                    <section className="mt-4">
                      <section className="mt-4">
                        If you become aware that any infringement on the rights
                        of {companyName} or third parties is occurring or if any
                        other use contrary to these General Conditions or the
                        Specific Conditions is taking place, we ask you to
                        inform us by sending us an email at privacy(at)
                        {serviceWebsite} or by telephone.
                      </section>
                    </section>
                  </section>
                </section>
              </section>
              <section className="mt-16" id="section05">
                <div className="mb-4 text-xl font-bold">
                  5. Publishing listings
                </div>
                <section className="mb-3">
                  <section className="mt-4">
                    You will need to register to be able to publish a listing
                    for your services on our Platform. By doing so, you
                    guarantee that all the data you include in the listing is
                    truthful, accurate, complete and up-to-date. At{' '}
                    {companyName}, we will strive to check the veracity and
                    accuracy of the information published in the listings, but
                    we cannot completely guarantee the veracity of such
                    information. Therefore, you undertake to keep the
                    information provided up-to-date at all times, notifying us
                    as quickly as possible if any of the information becomes
                    outdated or is inaccurate. By publishing a listing you
                    acknowledge that you are the owner of the service you are
                    listing (or duly authorised to list it by the owner of the
                    service) and that you will be solely responsible for the
                    truthfulness of the information published, and consequently
                    you will be exclusively liable for any damages or any other
                    liability that may arise in this regard.
                  </section>
                  <section className="mt-4">
                    You acknowledge that {companyName} reserves the right to
                    deactivate listings that do not comply with these General
                    Conditions.
                  </section>
                  <section className="mt-4">
                    {companyName}, as a simple intermediary, cannot be linked in
                    any way other than as described in these General Conditions
                    and in the applicable laws, and as such, the price and other
                    information contained in the listings regarding the
                    Professional Services does not constitute a commercial offer
                    on our part.
                  </section>
                  <section className="mt-4">
                    To publish a listing, you simply need to follow the steps
                    outlined on our Platform. Upon completion of the process to
                    publish a listing, if you are a private advertiser, you will
                    need to validate the telephone number supplied in the
                    listing and you will receive a confirmation email at the
                    address provided during registration. This validation may
                    carry an additional cost if the telephone number supplied is
                    a landline or an international number.
                  </section>
                  <section className="mb-3 mt-6">
                    <div className="mb-6 text-lg font-bold">
                      Free services for private users
                    </div>
                    <section className="mt-4">
                      <section className="mt-4">
                        If you are a Private Advertiser, you recognize that the
                        use of the free service for publication of listings is
                        limited to the publication of a maximum of two services,
                        so that you can test our service. You can maintain these
                        services published for as long as you want.
                      </section>
                      <section className="mt-4">
                        Once you have used up your quota of free listings, if
                        you want to publish an additional service, you must
                        purchase one of the paid publication services on{' '}
                        {serviceName}.
                      </section>
                      <section className="mt-4">
                        {companyName} may keep some data blocked from the date
                        of deactivation of the last listing in order to prevent
                        misuse of the free service, or fraud. See our{' '}
                        <a
                          data-test-id="privacy-policy-link"
                          href={toLocalizedRoute(ROUTES.PRIVACY_POLICY_ROUTE)}
                          className="cursor-pointer text-sky-600 hover:text-sky-400"
                        >
                          Privacy Policy
                        </a>{' '}
                        for more information.
                      </section>
                    </section>
                  </section>
                  <section className="mb-3 mt-6">
                    <div className="mb-6 text-lg font-bold">
                      Publication of listings and other services for
                      Professional Users
                    </div>
                    <section className="mt-4">
                      <section className="mt-4">
                        The services offered for Professional Users,
                        (hereinafter referred to as{' '}
                        <span className="font-bold">
                          &quot;Professional User(s)&quot;
                        </span>
                        ), including, but not limited to, the publication of
                        listings, are subject to the subscription of a contract
                        of services and to the economic consideration
                        established for each of the services for professionals
                        at any given time.
                      </section>
                      <section className="mt-4">
                        The listings of Professional Users may also be
                        published, at the sole discretion of {companyName}, on
                        other professional services listing portals unless the
                        Professional User expressly objects to such publication.
                        In addition, in the event that the Professional User has
                        contracted other services, this user may connect his or
                        her {serviceName}/tools account and import part of the
                        content.
                      </section>
                    </section>
                  </section>
                </section>
              </section>
              <section className="mt-16" id="section06">
                <div className="mb-4 text-xl font-bold">
                  6. Rules for publishing listings
                </div>
                <section className="mb-3">
                  <section className="mb-3">
                    <div>
                      By using the Platform for publishing listings, you
                      expressly agree to:
                    </div>
                    <ol className="my-6 ml-6 list-disc">
                      <li className="mb-1">
                        Not republish images or publish images with any kinds of
                        frame, watermarks, logos, text, or videos made by means
                        of photo montages of professional services.
                      </li>
                      <li className="mb-1">
                        Not publish images or other types of multimedia content
                        (videos) that are not relevant to the listing.
                      </li>
                      <li className="mb-1">
                        Not publish listings that contain images or any other
                        type of multimedia content (videos) that do not
                        correspond to the advertised professional service or
                        that are not related to the advertiser&apos;s activity.
                      </li>
                      <li className="mb-1">
                        Not publish multimedia content of its own or generated
                        by third parties, by contracting the corresponding
                        viewer service, with inappropriate, defamatory, illicit
                        themes, materials or information, that violate any third
                        party rights (including, in particular, image rights,
                        data protection or intellectual or industrial property);
                        that contain contact data of the advertiser, watermarks,
                        logos or texts; or that are irrelevant or unrelated to
                        the advertised professional service.
                      </li>
                      <li className="mb-1">
                        Not include in the listing description irrelevant
                        information or information unrelated to the advertised
                        professional service.
                      </li>
                      <li className="mb-1">
                        Not use capital letters in the ad description, beyond
                        the proper use at the spelling level.
                      </li>
                      <li className="mb-1">
                        Not use capital letters in the ad description, beyond
                        the proper use at the spelling level.
                      </li>
                      <li className="mb-1">
                        Not add additional telephone numbers or contact emails
                        outside the space intended for that purpose in the
                        listing.
                      </li>
                      <li className="mb-1">
                        Not publish professional services that will not be
                        available in the near future or currently unavailable.
                      </li>
                      <li className="mb-1">
                        Not publish listings concerning the transfer or
                        assignment of licenses, authorisations, concessions and
                        similar.
                      </li>
                      <li className="mb-1">
                        In general, engaging in any artificial conduct with the
                        aim of obtaining an unlawful positioning in the listings
                        of the Platform and/or causing fraudulent alerts to be
                        sent to Users (e.g. posting and subsequently deleting
                        photographs, altering the description of a listing
                        repeatedly and without justification).
                      </li>
                      <li className="mb-1">Not publish inaccurate prices.</li>
                      <li className="mb-1">
                        You cannot take down a listing with a code and publish
                        it again with a different code.
                      </li>
                      <li>
                        You may not publish advertisements with a
                        content/objective other than the sale of the specific
                        professional service advertised
                      </li>
                      <li>
                        If you are a member of a franchise network, professional
                        association, multi-agency or any other organisation of a
                        similar nature (whether operating under the same or a
                        different VAT) - (you undertake not to) duplicate the
                        same listing that has already been published on the
                        Platform by other franchisee members, associates or
                        agents of your organisation during the same period of
                        time; and this regardless of whether the listings are
                        published under different accounts, trademarks, logos or
                        trade names, as well as under separate publishing
                        contracts or even regardless of the authorisation of one
                        of them and/or the mandate of the professional service
                        owner.
                      </li>
                    </ol>
                    <section className="mb-3">
                      In the event that even a single violation of the rules for
                      publishing listings is detected or occurs, you accept that{' '}
                      {companyName} reserves the unquestionable right to
                      terminate and/or suspend the contractual relationship, the
                      provision of the service and/or remove the advertisements
                      that do not comply with the publication rules and/or the
                      General Conditions and/or the Specific Conditions, if
                      applicable.
                    </section>
                    <section className="mb-3">
                      You accept that {companyName} reserves the right to remove
                      listings that do not comply with these rules of
                      publication or the General Conditions and/or the Specific
                      Conditions, where applicable.
                    </section>
                    <section className="mb-3 mt-6">
                      <div className="mb-6 text-lg font-bold">
                        Anti-fraud measures - measures taken in the event of
                        non-compliance with these General Terms and Conditions
                      </div>
                      <section className="mt-4">
                        <section className="mt-4">
                          In order to prevent fraudulent use of the Platform,
                          you understand and accept that {companyName} may
                          suspend and/or penalise positioning or visibility of
                          listings that contain material suspected of being
                          fraudulent or contrary to these General Conditions.
                          These suspended listings will not be visible on the
                          Platform until the appropriate checks are completed.
                        </section>
                        <section className="mt-4">
                          In order to provide accurate information to our Users,{' '}
                          {companyName} may penalise advertisers who do not
                          comply with the publication rules indicated above in
                          article 6.
                        </section>
                        <section className="mt-4">
                          The listings penalised for these reasons will lose all
                          the ranking values they had acquired at the time of
                          the application of the penalty and will not appear in
                          the Users alerts. In this regard, the penalty will be
                          applied on a daily basis to be determined by{' '}
                          {companyName}, depending on the behaviour and warnings
                          given to the advertiser, and may be extended up to 30
                          days.
                        </section>
                        <section className="mt-4">
                          Penalties arising from misuse of our Platform will be
                          notified to the advertiser and will apply to all
                          listings posted at that time from the relevant account
                          on the Platform.
                        </section>
                        <section className="mt-4">
                          {companyName} will have the right, but not the
                          obligation, to investigate reports of fraud notified
                          by users and, in this case, the advertiser must
                          cooperate in the investigation.
                        </section>
                        <section className="mt-4">
                          <div>
                            By using the publication service, you understand and
                            accept that {companyName} will not be liable for any
                            direct or indirect loss or damage due to the
                            fraudulent use of the Platform and, in particular,
                            to the application of anti-fraud mechanisms and
                            measures, including but not limited to:
                          </div>
                          <ol className="my-6 ml-6 list-disc">
                            <li className="mb-1">
                              loss of earnings or profits
                            </li>
                            <li className="mb-1">loss of a business</li>
                            <li className="mb-1">
                              loss of profits or contracts
                            </li>
                            <li className="mb-1">loss of data</li>
                          </ol>
                        </section>
                        <section className="mt-4">
                          {companyName} reserves the right to implement any
                          additional anti-fraud measures it deems appropriate or
                          necessary from time to time.
                        </section>
                        <section className="mt-4">
                          In accordance with these General Conditions, you
                          expressly accept that, once the listing is published,
                          with your consent at the end of the publication
                          process, you will lose the right of withdrawal, in
                          accordance with the provisions of the Consumer Code
                          and applicable legislation.
                        </section>
                      </section>
                    </section>
                  </section>
                </section>
              </section>
              <section className="mt-16" id="section07">
                <div className="mb-4 text-xl font-bold">
                  7. Additional services
                </div>
                <section className="mb-3">
                  <div>
                    As mentioned above, apart from the use of the Platform and
                    services to connect people offering professional services
                    with those looking for it by publishing listings, we also
                    provide a number of Additional Services, directly or through
                    other companies (from our group or not) with which we
                    collaborate. Specifically, we offer you:
                  </div>
                  <ol className="my-6 ml-6 list-disc">
                    <li className="mb-1">
                      A service called API (Application Program Interface)
                    </li>
                    <li className="mb-1">
                      Multimedia services, including photo albums and videos
                    </li>
                    <li className="mb-1">
                      A webinar participation service for professionals
                    </li>
                    <li className="mb-1">
                      An export service for professional services listings
                    </li>
                    <li className="mb-1">
                      An information service on similar professional services
                      that may be of interest to you
                    </li>
                    <li className="mb-1">
                      A duplicate listing monitoring service to identify
                      duplicate listings from different public sources on the
                      web.
                    </li>
                  </ol>
                  <section className="mt-4">
                    Please note that these Additional Services may be subject to
                    Specific Conditions that are different to these General
                    Conditions.
                  </section>
                </section>
              </section>
              <section className="mt-16" id="section08">
                <div className="mb-4 text-xl font-bold">
                  8. Intellectual and industrial property
                </div>
                <section className="mb-3">
                  <section className="mb-3 mt-6">
                    <div className="mb-6 text-lg font-bold">
                      8.1. Material and information contained on the Platform
                    </div>
                    <section className="mt-4">
                      Through these General Conditions, you acknowledge and
                      accept that all rights regarding the material and elements
                      appearing on the Platform (including, but not limited to,
                      drawings, text, graphics, maps, photographs, images, audio
                      material, video material, distinguishing marks, logos,
                      trademarks, trade names, domains, software etc.) belong to{' '}
                      {companyName} or, where appropriate, to third parties who
                      have consented to the transfer of the same to{' '}
                      {companyName}, consequently, we have all the necessary
                      rights and licenses for the use of such.
                    </section>
                    <section className="mt-4">
                      The fact that we allow you to access, use, browse, utilise
                      and/or download material from our Platform in no way
                      implies that we waive, transmit or grant any licenses or
                      transfer of our rights, or that you acquire ownership
                      thereof. As a User or a Registered User, you only have the
                      right to strictly personal and private use, solely in
                      order to benefit from the Platform. Consequently, you
                      cannot manipulate any identifying element from{' '}
                      {companyName} or the respective owners. In particular, the
                      use of any material or elements from the Platform for
                      inclusion in whole or in part on other websites or apps
                      without our prior written permission is prohibited. The
                      use of any element or information in such a way so as to
                      give the impression that it belongs to or is associated
                      with the User or Registered User is likewise prohibited.
                    </section>
                    <section className="mt-4">
                      Specifically, it is not permitted to re-sell, deep link,
                      use, copy, monitor (for example, spider, scrape), display,
                      download, save or reproduce the content, information,
                      software, products or services available on our website
                      for any commercial or competitive activity without our
                      prior written permission.
                    </section>
                  </section>
                  <section className="mb-3 mt-6">
                    <div className="mb-6 text-lg font-bold">
                      8.2. Material published by the User
                    </div>
                    <section className="mt-4">
                      The material and information you publish on our Platform
                      when using the Professional Services must respect the
                      image rights, data protection and intellectual and
                      industrial property that may exist on them. Therefore, you
                      acknowledge and agree to assume exclusive responsibility
                      for any liability or damage that may arise in connection
                      therewith.
                    </section>
                    <section className="mt-4">
                      By disseminating or publishing information, non-personal
                      data, photographs or any other material through{' '}
                      {serviceName}, you grant us a non-exclusive, royalty-free,
                      perpetual, transferable, valid and unlimited worldwide
                      license for the maximum term of protection permitted by
                      law and with the ability to sub-license such information
                      to affiliated companies and third parties. In this sense
                      you acknowledge that {companyName} has the right to
                      disseminate all or part of the content of listings for
                      advertising campaigns, promotion of the Platform, blogs,
                      etc.
                    </section>
                    <section className="mt-4">
                      <div>Specifically, you authorise us to:</div>
                      <ol className="my-6 ml-6 list-disc">
                        <li className="mb-1">
                          add {serviceName}&apos;s watermarks in order to
                          prevent such photographs from being published by
                          unauthorised third parties
                        </li>
                        <li className="mb-1">
                          use information about the professional services
                          advertised on our Platform for statistical reports and
                          other references in the related markets. Such
                          information may be used even after the respective
                          listings had been removed from the Platform
                        </li>
                        <li className="mb-1">
                          copy, distribute, transform or publicly communicate
                          said material
                        </li>
                      </ol>
                    </section>
                    <section className="mt-4">
                      If you are a photographer and use and/or access our
                      Platform to upload photos, videos related to professional
                      services, which are the result of your work or service, by
                      disseminating or publishing information, non-personal
                      data, photographs or any other material through{' '}
                      {serviceName}, you grant us a non-exclusive, royalty-free,
                      perpetual, transferable, valid and unlimited worldwide
                      license for the maximum term of protection permitted by
                      law and with the ability to sub-license such information
                      to affiliated companies and third parties. In this sense
                      you acknowledge that {companyName} has the right to
                      disseminate all or part of the content of listings for
                      advertising campaigns, promotion of the Platform, blogs,
                      etc.
                    </section>
                  </section>
                </section>
              </section>
              <section className="mt-16" id="section09">
                <div className="mb-4 text-xl font-bold">
                  9. {companyName}&apos;s responsibility
                </div>
                <section className="mb-3">
                  <section className="mt-4">
                    Our responsibility is established in the terms of the
                    regulations contained in Italian Legislative Decree 70/2003
                    and subsequent amendments and integrations in implementation
                    of Directive 2000/31/EC on electronic commerce, which
                    states, <span className="italic">inter alia</span>, that we
                    cannot be responsible for the content published by users.
                  </section>
                  <section className="mt-4">
                    As mere intermediary service providers, although we do our
                    best to ensure that all published content is correct, we
                    have no obligation to exercise prior controls over the
                    veracity, accuracy, lawfulness, completeness,
                    appropriateness or suitability of the information or
                    material published on the Platform as this is information
                    stored at the request of Users. However, if for any reason
                    we become aware of any unlawful actions or actions contrary
                    to these General Conditions or the Specific Conditions, we
                    will act with due diligence to remove the relevant
                    information or make it impossible to access in accordance
                    with current regulations.
                  </section>
                  <section className="mt-4">
                    {companyName} assumes no kind of liability for damages that
                    may arise in connection with the relationship between users
                    arising from their contact or negotiations.
                  </section>
                  <section className="mt-4">
                    The Additional Services will be regulated according to their
                    specific conditions, with {companyName} assuming the
                    liability due under the applicable law in connection with
                    the same.
                  </section>
                  <section className="mt-4">
                    Access to and use of the Platform requires computer
                    equipment or a computing device and an Internet connection,
                    the costs of which you agree to bear yourself.
                  </section>
                  <section className="mt-4">
                    We are not liable for any charges, encumbrance, claims,
                    conflicts or any other charges that could affect the
                    professional services listed on our Platform, and you agree
                    to hold {companyName} harmless with respect to any liability
                    or damages thereby arising.
                  </section>
                  <section className="mt-4">
                    We are not responsible for the veracity, integrity,
                    accuracy, lawfulness, appropriateness or suitability of the
                    information, materials and/or content available on the
                    websites of third parties which you may access through links
                    on our Platform.
                  </section>
                </section>
              </section>
              <section className="mt-16" id="section10">
                <div className="mb-4 text-xl font-bold">
                  10. Payments on the Platform
                </div>
                <section className="mb-3">
                  <section className="mt-4">
                    On the Platform you can contract some of our Professional
                    Services such as, for example, the publication of your
                    listings, if you are a Registered User, and pay directly by
                    credit or debit card. These payment instruments are subject
                    to the authorisation of the corresponding issuing entities,
                    so that, in the absence of such authorisation, {companyName}{' '}
                    will not be able to carry out the transaction and will have
                    to cancel it.
                  </section>
                  <section className="mt-4">
                    {companyName} uses an external provider for electronic
                    payment. The data provided to {companyName} through the
                    payment channel is duly encrypted to guarantee maximum
                    security and confidentiality by means of the &quot;Secure
                    Socket Layer&quot; (SSL) system.
                  </section>
                </section>
              </section>
              <section className="mt-16" id="section11">
                <div className="mb-4 text-xl font-bold">
                  11. Contracting with third parties through the Platform
                </div>
                <section className="mb-3">
                  <section className="mt-4">
                    You acknowledge and accept that any contractual or
                    non-contractual relationship, as appropriate, that you
                    formalise with any advertiser, User, Registered User or
                    third party through our Platform or our Professional
                    Services will be carried out solely and exclusively between
                    you and the other party in question. {companyName} will not
                    be part of, or assume any liability whatsoever for, such a
                    relationship or for any damages that might arise from it.
                  </section>
                  <section className="mt-4">
                    Therefore, we advise you to proceed with caution when
                    contacting and contracting any advertiser, User, Registered
                    User or third party through our Platform. If you have any
                    concerns or suspicions that any kind of unlawful activity,
                    fraud or any other actions contrary to these General
                    Conditions are being carried out, we advise you not to
                    formalise any kind of payment or contract and get in touch
                    with us as soon as you become aware of any of the above
                    activity (by sending us an email at privacy(at)
                    {serviceWebsite}), and we will do everything possible to
                    help resolve the situation.
                  </section>
                </section>
              </section>
              <section className="mt-16" id="section12">
                <div className="mb-4 text-xl font-bold">
                  12. Links or hyperlinks to the Platform
                </div>
                <section className="mb-3">
                  <section className="mt-4">
                    At {companyName}, we are concerned about the integrity of
                    our Platform when they are accessed in a particular way
                    solely by third parties or when they are displayed in an
                    area created exclusively by third parties. In this regard,
                    you must explicitly request and ensure you have the express
                    written permission of {companyName} before deep linking or
                    framing this Website or any of its Contents. If you would
                    like to do this, when contacting {companyName} to request
                    permission you will need to include: a) your name, email
                    address and telephone number, b) the name of your company,
                    c) the address or addresses of the website where the
                    proposed deep linking or framing will appear, d) specific
                    information on the deep linking or framing covered,
                    including the content or pages of the website with which you
                    want to establish the deep linking or framing.
                  </section>
                  <section className="mt-4">
                    You must not link our Platform with any other website or app
                    containing inappropriate, defamatory or illegal information,
                    material or themes, or material or information that
                    infringes on any third party&apos;s rights, including any
                    intellectual or industrial property rights. Likewise, you
                    may not set up any kind of framing that allows the Platform
                    to be viewed through an address other than that of the
                    actual Platform in such a way that or that could create
                    errors or confuse or mislead users as regards the true
                    source of the items or services being displayed, or that
                    involves an act of unfair competition (e.g. that can be used
                    to exploit {companyName}&apos;s reputation and prestige, or
                    otherwise, contrary to the law).
                  </section>
                </section>
              </section>
              <section className="mt-16" id="section13">
                <div className="mb-4 text-xl font-bold">
                  13. Personal data protection
                </div>
                <section className="mb-3">
                  <section className="mt-4">
                    When you access the Platform and/or use the Professional
                    Services or Additional Services, it is possible that we may
                    process your personal data. You can get detailed information
                    about how and why we process your personal data in our{' '}
                    <a
                      data-test-id="cookie-policy-link"
                      href={toLocalizedRoute(ROUTES.PRIVACY_POLICY_ROUTE)}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Privacy Policy
                    </a>
                    .
                  </section>
                  <section className="mt-4">
                    We also hereby notify you that we may use cookies when you
                    browse the Platform. You can get detailed information about
                    our use of cookies in our{' '}
                    <a
                      data-test-id="cookie-policy-link"
                      href={toLocalizedRoute(ROUTES.COOKIE_POLICY_ROUTE)}
                      className="cursor-pointer text-sky-600 hover:text-sky-400"
                    >
                      Cookie Policy
                    </a>
                    .
                  </section>
                </section>
              </section>
              <section className="mt-16" id="section14">
                <div className="mb-4 text-xl font-bold">
                  14. Term and Duration
                </div>
                <section className="mb-3">
                  <section className="mt-4">
                    Access to the Platform and Professional Services is, in
                    principle, indefinite.
                  </section>
                  <section className="mt-4">
                    Both the User and {companyName} may terminate their
                    relationship without prior notice at any time.
                  </section>
                  <section className="mt-4">
                    However, a different duration and the need for prior notice
                    may be established in the Specific Conditions, in the
                    contract signed by professional advertisers or in the
                    applicable legislation. Any Clause of these General
                    Conditions that is or that becomes illegal, invalid or
                    unenforceable will be excluded and deemed inapplicable,
                    being replaced by another clause that resembles it as
                    closely as possible, without this negatively affecting the
                    User, and without giving rise to any compensation
                    whatsoever. In this case, the remaining provisions or
                    clauses are not affected and will remain in full force.
                  </section>
                </section>
              </section>
              <section className="mt-16" id="section15">
                <div className="mb-4 text-xl font-bold">
                  15. Applicable law and jurisdiction
                </div>
                <section className="mb-3">
                  <section className="mt-4">
                    These General Conditions, as well as any relationship
                    between {companyName} and the Users and Registered Users are
                    subject to Italian law. Any dispute, within the limits of
                    the legislation in force, even of an interpretative nature,
                    which may arise with reference to the General Conditions
                    will therefore be referred to the jurisdiction of the Court
                    of Rome.
                  </section>
                </section>
              </section>
            </div>
            <hr className="my-12 h-px border-0 bg-gray-200 dark:bg-gray-700" />
            <div>
              <section className="my-6">
                In accordance with and for the purposes of articles 1341 and
                1342 of the Italian Civil Code, you confirm that you have read
                and expressly approved the following articles: 2 (Access and
                Acceptance of the General Conditions), 4 (Conditions of Use and
                Liability of the Platform), 6 (Rules for the Publication of
                Listings), 8 (Intellectual and Industrial Property), 9 (
                {companyName}&apos;s Liability), 15 (Applicable Law and
                Jurisdiction).
              </section>
            </div>
            <hr className="my-12 h-px border-0 bg-gray-200 dark:bg-gray-700" />
            <div>
              <section className="my-6">
                We know that reading this all the way to the end has taken you a
                long time. So thank you. 🙏
              </section>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
